import { useMemo, useState } from 'react';
import { Activity, Trash2, X } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { useAppEvents, type AppModule } from '@/store/appEvents';
import { cn } from '@/lib/utils';

const SHOWN = 100;

const MODULE_TONE: Record<AppModule, 'accent' | 'default' | 'good' | 'warn' | 'critical'> = {
  pilot: 'accent',
  assets: 'warn',
  sitl: 'good',
  diva: 'default',
  settings: 'default',
  system: 'default',
};

const SEVERITY_COLOR = {
  info: 'var(--color-fg-secondary)',
  warning: 'var(--color-status-warn)',
  critical: 'var(--color-status-critical)',
} as const;

function fmtClock(ts: number): string {
  return new Date(ts).toLocaleTimeString([], { hour12: false });
}

/**
 * Events center — TopBar popover over the app-wide event register
 * (`store/appEvents`). Every module reports here: pilot commands + robot
 * events, assets configuration, SITL spawns, Diva prompts, settings changes.
 * Rows carry the module badge and, when a robot was involved, its name.
 */
export function EventsCenter() {
  const events = useAppEvents((s) => s.events);
  const clear = useAppEvents((s) => s.clear);
  const [open, setOpen] = useState(false);
  const [moduleFilter, setModuleFilter] = useState<AppModule | null>(null);

  const list = useMemo(() => {
    const all = events.slice(0, SHOWN);
    return moduleFilter ? all.filter((e) => e.module === moduleFilter) : all;
  }, [events, moduleFilter]);

  const modulesPresent = useMemo(
    () => Array.from(new Set(events.map((e) => e.module))),
    [events],
  );
  const attention = useMemo(
    () => events.slice(0, SHOWN).filter((e) => e.severity !== 'info').length,
    [events],
  );

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Events"
        className={cn(
          'relative grid h-7 w-7 place-items-center rounded text-fg-secondary hover:bg-surface-2 hover:text-fg-primary',
          attention > 0 && 'text-status-warn hover:text-status-warn',
        )}
      >
        <Activity className="h-4 w-4" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <aside
            role="dialog"
            aria-label="Events"
            className="absolute right-0 top-full z-50 mt-1 flex w-[400px] flex-col rounded-lg border border-border-default bg-surface-1 shadow-[var(--shadow-pop)]"
          >
            <header className="flex items-center gap-2 border-b border-border-default px-3 py-2">
              <Activity className="h-4 w-4 text-accent" />
              <h2 className="text-[12.5px] font-semibold text-fg-primary">Events</h2>
              <button
                type="button"
                onClick={clear}
                title="Clear events"
                className="ml-auto grid h-6 w-6 place-items-center rounded text-fg-muted hover:bg-surface-2 hover:text-fg-primary"
              >
                <Trash2 className="h-3 w-3" />
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="grid h-6 w-6 place-items-center rounded text-fg-secondary hover:bg-surface-2 hover:text-fg-primary"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </header>

            {modulesPresent.length > 1 && (
              <div className="flex flex-wrap items-center gap-1 border-b border-border-subtle px-3 py-1.5">
                <FilterPill active={moduleFilter === null} onClick={() => setModuleFilter(null)}>
                  all
                </FilterPill>
                {modulesPresent.map((mod) => (
                  <FilterPill
                    key={mod}
                    active={moduleFilter === mod}
                    onClick={() => setModuleFilter(moduleFilter === mod ? null : mod)}
                  >
                    {mod}
                  </FilterPill>
                ))}
              </div>
            )}

            <ul className="max-h-[60vh] flex-1 divide-y divide-border-subtle overflow-y-auto overflow-x-hidden">
              {list.length === 0 ? (
                <li className="px-3 py-8 text-center text-[12px] text-fg-muted">
                  <Activity className="mx-auto mb-1.5 h-5 w-5 opacity-60" />
                  No events yet.
                </li>
              ) : (
                list.map((e) => (
                  <li key={e.id} className="px-3 py-1.5">
                    <div className="flex items-center gap-1.5">
                      <Badge tone={MODULE_TONE[e.module]}>{e.module}</Badge>
                      {e.robot_name ? (
                        <span
                          className="truncate font-mono text-[10px] text-fg-secondary"
                          title={e.robot_id}
                        >
                          {e.robot_name}
                        </span>
                      ) : (
                        e.robot_id && (
                          <span className="truncate font-mono text-[10px] text-fg-muted">
                            {e.robot_id}
                          </span>
                        )
                      )}
                      <span className="ml-auto flex-none font-mono text-[10px] tabular-nums text-fg-muted">
                        {fmtClock(e.ts)}
                      </span>
                    </div>
                    <div
                      className="mt-0.5 truncate font-mono text-[11px]"
                      style={{ color: SEVERITY_COLOR[e.severity] }}
                    >
                      {e.summary}
                    </div>
                  </li>
                ))
              )}
            </ul>
          </aside>
        </>
      )}
    </div>
  );
}

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded border px-1.5 py-0.5 font-mono text-[9.5px] uppercase tracking-wider transition-colors',
        active
          ? 'border-accent bg-accent/15 text-accent'
          : 'border-border-default bg-surface-2 text-fg-secondary hover:bg-surface-3 hover:text-fg-primary',
      )}
    >
      {children}
    </button>
  );
}
